import { useIsPlatformDictationEnabled } from '@/composition/hosts/dictation/hooks/useIsPlatformDictationEnabled';
import { IS_PLATFORM_DICTATION_ENABLED } from '@/features/flags/legacy-flags';
import {
  isShadowHeidiEnabled,
  useIsShadowHeidiEnabled,
} from '@/features/shadowHeidi/hooks/useIsShadowHeidiEnabled';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { getCurrentUser } from '@heidi/workspace/data';

export const isDictationEnabled = (): boolean =>
  !isShadowHeidiEnabled() &&
  IS_PLATFORM_DICTATION_ENABLED() &&
  Boolean(getCurrentUser()?.dictation?.enabled?.value);

export const useIsDictationEnabled = (): boolean => {
  // UKI-218: Shadow Heidi mode hides dictation surfaces — clinicians type
  // their note directly in the Research note tab.
  const isShadowHeidi = useIsShadowHeidiEnabled();
  // Ruling 36 layer (c): this used to re-read both platform keys through the
  // raw `useFeatureFlagEnabled` mechanism, duplicating the getter one line
  // above it verbatim — two more minting sites for keys that already had a
  // named owner, and a second place to forget when the rollout changes.
  const isPlatformFlagEnabled = useIsPlatformDictationEnabled();
  const userDictationEnabled =
    useQueryUser((user) => Boolean(user.dictation?.enabled?.value)).data ?? false;

  if (isShadowHeidi) return false;
  if (!isPlatformFlagEnabled) return false;

  return userDictationEnabled;
};

/**
 * Whether dictation settings should appear in product (rollout gate only).
 * Uses the same platform-specific PostHog flag as {@link useIsDictationEnabled}
 * (`release-desktop-dictation` on Tauri, `universal-dictation` on web) — does not
 * read user preference. Delegates to `useIsPlatformDictationEnabled`, which lives
 * in its own module so it can also be imported by `@/features/flags` (see that
 * module's doc comment for why).
 */
export const useIsDictationSettingsEnabled = (): boolean =>
  useIsPlatformDictationEnabled();
